/**
 * WorkflowHistoryPage — Org Admin only.
 *
 * One row per record (workflow instance). Click a row → detail page with all
 * steps, approvers, comments, and the full form data.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClockCounterClockwise, FunnelSimple, CaretLeft, CaretRight, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONE = {
    pending: "border-warning text-warning",
    approved: "border-success text-success",
    rejected: "border-destructive text-destructive",
};

export default function WorkflowHistoryPage() {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ form_id: "__all__", status: "__all__", date_from: "", date_to: "" });
    const [forms, setForms] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(25);
    const [total, setTotal] = useState(0);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    const load = async (resetPage = false) => {
        const p = resetPage ? 1 : page;
        if (resetPage) setPage(1);
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.form_id && filters.form_id !== "__all__") params.set("form_id", filters.form_id);
            if (filters.status && filters.status !== "__all__") params.set("status", filters.status);
            if (filters.date_from) params.set("date_from", filters.date_from);
            if (filters.date_to) params.set("date_to", filters.date_to);
            params.set("page", String(p));
            params.set("limit", String(limit));
            const { data } = await api.get(`/workflow-history?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/workflow-history/forms");
                setForms(data);
            } catch (e) { console.warn("[wf-history] failed to load forms list:", e); }
        })();
    }, []);

    useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);
    useEffect(() => { load(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const resetFilters = () => {
        setFilters({ form_id: "__all__", status: "__all__", date_from: "", date_to: "" });
        setPage(1);
        setTimeout(() => load(true), 0);
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Audit"
                title="Workflow history"
                description="Every record that ran through an approval workflow. Click an instance to see the full step-by-step trail."
                testid="wf-history-header"
            />

            <div className="brut-section p-4 mb-6" data-testid="wf-history-filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                    <div>
                        <Label className="label-overline flex items-center gap-1"><FunnelSimple size={11} /> Form</Label>
                        <Select value={filters.form_id} onValueChange={(v) => setFilters({ ...filters, form_id: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid="wf-history-form-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All forms</SelectItem>
                                {forms.map((f) => (
                                    <SelectItem key={f.id} value={f.id}>{f.form_title || f.form_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-overline">Status</Label>
                        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid="wf-history-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Any status</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-overline">From date</Label>
                        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-2 rounded-sm" data-testid="wf-history-from" />
                    </div>
                    <div>
                        <Label className="label-overline">To date</Label>
                        <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-2 rounded-sm" data-testid="wf-history-to" />
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => load(true)} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground flex-1" data-testid="wf-history-apply"><FunnelSimple size={14} className="mr-1" /> Apply</Button>
                        <Button variant="outline" onClick={resetFilters} className="rounded-sm" data-testid="wf-history-reset" title="Reset filters"><ArrowsClockwise size={14} /></Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <EmptyState icon={ClockCounterClockwise} title="Nothing to show yet" description="No workflow instances match your filters." testid="wf-history-empty" />
            ) : (
                <>
                    <div className="brut-section overflow-x-auto">
                        <table className="w-full" data-testid="wf-history-table">
                            <thead><tr>
                                <th className="data-header text-left">Instance ID</th>
                                <th className="data-header text-left">Workflow / Form</th>
                                <th className="data-header text-left">Submitted by</th>
                                <th className="data-header text-left">Submitted on</th>
                                <th className="data-header text-left">Current step</th>
                                <th className="data-header text-left">Status</th>
                                <th className="data-header"></th>
                            </tr></thead>
                            <tbody>
                                {items.map((row, i) => (
                                    <tr
                                        key={row.instance_id}
                                        className="hover:bg-secondary/50 cursor-pointer"
                                        onClick={() => navigate(`/workflow-history/${row.record_id}`)}
                                        data-testid={`wf-history-row-${i}`}
                                    >
                                        <td className="data-cell">
                                            <code className="text-xs font-mono text-muted-foreground" title={row.instance_id}>{row.instance_id.slice(0, 10)}…</code>
                                        </td>
                                        <td className="data-cell">
                                            <div className="font-medium text-sm">{row.form_title || row.form_name}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground">{row.form_code}</div>
                                        </td>
                                        <td className="data-cell text-sm">{row.submitted_by_name || row.submitted_by_email || "—"}</td>
                                        <td className="data-cell font-mono text-xs">{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "—"}</td>
                                        <td className="data-cell text-xs">
                                            {row.status === "pending"
                                                ? <span>{row.current_step_index + 1} / {row.total_steps} · <span className="font-medium">{row.current_step_name || "—"}</span></span>
                                                : <span className="text-muted-foreground">— ({row.total_steps} steps)</span>}
                                        </td>
                                        <td className="data-cell">
                                            <Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${STATUS_TONE[row.status] || ""}`}>{row.status}</Badge>
                                        </td>
                                        <td className="data-cell text-right">
                                            <Button variant="ghost" size="icon" className="rounded-sm" data-testid={`wf-history-view-${i}`}>
                                                <ArrowRight size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between mt-4 text-sm" data-testid="wf-history-pager">
                        <div className="text-muted-foreground">Showing <span className="font-mono">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of <span className="font-mono">{total}</span></div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="wf-history-prev"><CaretLeft size={14} /></Button>
                            <span className="font-mono text-xs">Page {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="wf-history-next"><CaretRight size={14} /></Button>
                        </div>
                    </div>
                </>
            )}
        </PageContainer>
    );
}
